import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AuditEntry } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Overlay";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { absoluteTime, humaniseAction, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/o/$orgId/audit")({
  component: AuditScreen,
});

interface AuditPage {
  entries: AuditEntry[];
  next_cursor?: string;
}

/**
 * The activity log.
 *
 * # WHY THIS IS A TABLE NOW
 *
 * It was a timeline, with a note saying these entries are read
 * chronologically and that a table would force them into the same columns.
 * The columns were the point: every entry has the same five facts — when,
 * what, to what, by whom, from where — and they were stacked two and three
 * lines deep per entry, so comparing two of them meant reading two paragraphs
 * instead of glancing down a column. Fifty entries was a wall of text.
 *
 * It is still chronological. A table does not stop being a timeline by having
 * aligned columns; it stops being a wall.
 *
 * # WHY THE DETAIL IS A DIALOG AND NOT AN EXPANDING ROW
 *
 * Because a before-and-after is wide. Expanding inside a row either squeezes
 * it into one column's width or makes that row four times the height of its
 * neighbours, and a row whose height depends on its contents is what makes a
 * table ragged. The dialog gets the whole width, and the rows stay one line
 * each, which is the rule this table idiom rests on.
 */
function AuditScreen() {
  const { orgId } = Route.useParams();
  const [showing, setShowing] = useState<AuditEntry | null>(null);

  const query = useInfiniteQuery({
    queryKey: ["audit", orgId],
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      api.get<AuditPage>(
        `/organisations/${orgId}/audit-log`,
        { cursor: pageParam || undefined, limit: 50 },
        signal,
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
  });

  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Head
        title="Activity"
        description="Every change made in this organisation, newest first. Entries are never edited or deleted."
      />

      <Panel padded={false}>
        {query.isLoading && <TableSkeleton rows={8} columns={5} />}
        {query.isError && (
          <div className="px-5 py-4">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        )}
        {query.data && entries.length === 0 && (
          <EmptyState
            title="Nothing has happened yet"
            description="Changes to members, projects and services will appear here as they happen."
          />
        )}

        {entries.length > 0 && (
          <div className="px-5 pb-1 pt-2">
            <Table dense fixed>
              <thead>
                <tr>
                  <Th width="w-[11ch]">When</Th>
                  <Th width="w-[18ch]">What</Th>
                  <Th>To what</Th>
                  {/* Wider than it reads, because this column carries two things:
                      who, and whether that who was one of us. "Drumlin staff"
                      is thirteen characters in the label face and the badge
                      must not be abbreviated — a chip reading "DRUMLI" is
                      worse than no chip. The address beside it truncates
                      instead. */}
                  <Th width="w-[28ch]">By whom</Th>
                  <Th width="w-[14ch]">From</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const detail = Boolean(entry.before || entry.after || entry.reason);
                  return (
                    <Tr
                      key={entry.id}
                      onClick={detail ? () => setShowing(entry) : undefined}
                    >
                      <Td clip title={absoluteTime(entry.created_at)}>
                        <span className="text-[var(--text-secondary)]">
                          {relativeTime(entry.created_at)}
                        </span>
                      </Td>
                      <Td clip>
                        <span className="inline-flex items-center gap-2">
                          {/* The outcome, where it reads as part of the action
                              rather than as a bullet in front of a paragraph. */}
                          <Outcome outcome={entry.outcome} />
                          <span className="truncate">{humaniseAction(entry.action)}</span>
                        </span>
                      </Td>
                      <Td clip title={entry.target_label || undefined}>
                        <span className="mono text-[12.5px] text-[var(--text-secondary)]">
                          {entry.target_label || "—"}
                        </span>
                      </Td>
                      <Td
                        clip
                        title={entry.actor_label || "system"}
                        secondary={
                          entry.actor_type === "platform_admin" ? (
                            <Chip tone="warn">Drumlin staff</Chip>
                          ) : entry.actor_type === "api_token" ? (
                            <Chip>API token</Chip>
                          ) : undefined
                        }
                      >
                        {entry.actor_label || "system"}
                      </Td>
                      <Td clip>
                        <span className="mono text-[12.5px] text-[var(--text-muted)]">
                          {entry.ip_address || "—"}
                        </span>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {query.hasNextPage && (
          <div className="border-t border-[var(--border)] px-5 py-3">
            <Button
              tone="ghost"
              size="dense"
              busy={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        )}
      </Panel>

      <Dialog
        open={showing !== null}
        onOpenChange={(open) => !open && setShowing(null)}
        title={showing ? humaniseAction(showing.action) : ""}
        size="lg"
      >
        {showing && (
          <div className="flex flex-col gap-4">
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[auto_1fr]">
              <Fact label="When">{absoluteTime(showing.created_at)}</Fact>
              <Fact label="By whom">{showing.actor_label || "system"}</Fact>
              {showing.target_label && (
                <Fact label="To what" mono>
                  {showing.target_label}
                </Fact>
              )}
              {showing.ip_address && (
                <Fact label="From" mono>
                  {showing.ip_address}
                </Fact>
              )}
              <Fact label="Outcome">{showing.outcome}</Fact>
            </dl>

            {showing.reason && (
              <p className="t-body italic text-[var(--text-secondary)]">{showing.reason}</p>
            )}

            {(showing.before || showing.after) && (
              <div className="grid gap-3 sm:grid-cols-2">
                {showing.before && <StateBlock title="Before" state={showing.before} />}
                {showing.after && <StateBlock title="After" state={showing.after} />}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}

function Fact({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="section-label">{label}</dt>
      <dd className={mono ? "mono text-[13px]" : "t-body"}>{children}</dd>
    </>
  );
}

/** The outcome, as a dot that sits on the text's own line. */
function Outcome({ outcome }: { outcome: AuditEntry["outcome"] }) {
  const token = outcome === "success" ? "running" : outcome === "denied" ? "degraded" : "failed";
  return (
    <span
      className="h-[6px] w-[6px] shrink-0 rounded-full"
      style={{ backgroundColor: LAMP_FOR[token] }}
      title={outcome}
      aria-label={outcome}
    />
  );
}

function StateBlock({ title, state }: { title: string; state: Record<string, unknown> }) {
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-3">
      <p className="section-label mb-1.5">{title}</p>
      <dl className="flex flex-col gap-1 text-[13px]">
        {Object.entries(state).map(([key, value]) => (
          <div key={key} className="flex justify-between gap-3">
            <dt className="text-[var(--text-muted)]">{key.replace(/_/g, " ")}</dt>
            <dd className="mono truncate text-[var(--text-primary)]">{String(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * The lamp a state name lights.
 *
 * The screens used to compose a custom property name out of a variable —
 * `var(--color-state-${token}-fg)` — which works until the token set is
 * replaced and then fails silently, because a missing custom property is not
 * an error, it is simply nothing. This is a lookup, so an unknown state is a
 * compile-time concern rather than an invisible one.
 */
const LAMP_FOR: Record<string, string> = {
  running: "var(--success)",
  healthy: "var(--success)",
  degraded: "var(--warning)",
  suspended: "var(--warning)",
  failed: "var(--danger)",
  stopped: "var(--text-faint)",
  building: "var(--primary)",
};
